import { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import { Clinic, CreateClinicData} from "@/services/clinic-service";
import {Company, fetchCompanies } from "@/services/company-service";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { onlyNumbers } from "@/utils/formatters";

interface CreateEditClinicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinic: Clinic | null;
  onSave: (data: CreateClinicData, id?: string) => Promise<void>;
}

export function CreateEditClinicDialog({
  open,
  onOpenChange,
  clinic,
  onSave,
}: CreateEditClinicDialogProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [cnpj, setCnpj] = useState("");
  const [name, setName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [phone, setPhone] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [address, setAddress] = useState("");
  const [additionalAddress, setAdditionalAddress] = useState("");
  const [cityName, setCityName] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [whapiToken, setWhapiToken] = useState("");
  const [whapiChannel, setWhapiChannel] = useState("");
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [whatsappProvider, setWhatsappProvider] = useState("whapi");
  const [metaPhoneNumberId, setMetaPhoneNumberId] = useState("");
  const [metaWabaId, setMetaWabaId] = useState("");
  const [metaBotPhoneNumberId, setMetaBotPhoneNumberId] = useState("");
  const [metaAccessToken, setMetaAccessToken] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => { loadCompanies(); }, []);

  useEffect(() => {
    if (clinic) {
      setCnpj(clinic.cnpj || "");
      setName(clinic.name || "");
      setTradeName(clinic.trade_name || "");
      setPhone(clinic.phone || "");
      setCellPhone(clinic.cellphone || "");
      setPostcode(clinic.postcode || "");
      setAddress(clinic.address || "");
      setAdditionalAddress(clinic.additional_address || "");
      setCityName(clinic.city_name || "");
      setStateCode(clinic.state_code || "");
      setCompanyId(clinic.company_id || "");
      setWhapiToken(clinic.whapi_token || "");
      setWhapiChannel(clinic.whapi_channel || "");
      setTelegramBotToken(clinic.telegram_bot_token || "");
      setTelegramChatId(clinic.telegram_chat_id || "");
      setWhatsappProvider(clinic.whatsapp_provider || "whapi");
      setMetaPhoneNumberId(clinic.meta_phone_number_id || "");
      setMetaWabaId(clinic.meta_waba_id || "");
      setMetaBotPhoneNumberId(clinic.meta_bot_phone_number_id || "");
      setMetaAccessToken("");
    } else {
      clearForm();
    }
  }, [clinic, open]);

  const loadCompanies = async () => {
    try {
      const data = await fetchCompanies(false, false, undefined, 1, 100);
      setCompanies(data);
      if (data.length === 1 && !clinic) setCompanyId(data[0].id);
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
    }
  };

  const clearForm = () => {
    setCnpj(""); setName(""); setTradeName(""); setPhone(""); setCellPhone("");
    setPostcode(""); setAddress(""); setAdditionalAddress(""); setCityName(""); setStateCode("");
    setCompanyId(companies.length === 1 ? companies[0].id : "");
    setWhapiToken(""); setWhapiChannel(""); setTelegramBotToken(""); setTelegramChatId("");
    setWhatsappProvider("whapi");
    setMetaPhoneNumberId(""); setMetaWabaId(""); setMetaBotPhoneNumberId(""); setMetaAccessToken("");
    setErrors({});
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const cleanCnpj = onlyNumbers(cnpj);
    if (cleanCnpj.length !== 14) newErrors.cnpj = "CNPJ inválido";
    if (!name.trim()) newErrors.name = "Nome é obrigatório";
    if (!tradeName.trim()) newErrors.tradeName = "Nome fantasia é obrigatório";
    const cleanPhone = onlyNumbers(phone);
    if (cleanPhone.length < 10 || cleanPhone.length > 11) newErrors.phone = "Telefone inválido";
    const cleanCellPhone = onlyNumbers(cellPhone);
    if (cleanCellPhone.length < 10 || cleanCellPhone.length > 11) newErrors.cellPhone = "Celular inválido";
    const cleanPostcode = onlyNumbers(postcode);
    if (cleanPostcode.length !== 8) newErrors.postcode = "CEP inválido";
    if (!address.trim()) newErrors.address = "Endereço é obrigatório";
    if (!cityName.trim()) newErrors.cityName = "Cidade é obrigatória";
    if (!stateCode.trim()) newErrors.stateCode = "Estado é obrigatório";
    if (!companyId) newErrors.companyId = "Empresa é obrigatória";
    const hasToken = whapiToken.trim().length > 0;
    const hasChannel = whapiChannel.trim().length > 0;
    if (hasToken !== hasChannel) {
      if (!hasToken) newErrors.whapiToken = "Token Whapi obrigatório quando Canal é informado";
      if (!hasChannel) newErrors.whapiChannel = "Canal Whapi obrigatório quando Token é informado";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => document.getElementById(Object.keys(newErrors)[0])?.focus(), 0);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const data: CreateClinicData = {
      cnpj: onlyNumbers(cnpj), name: name.trim(), trade_name: tradeName.trim(),
      phone: onlyNumbers(phone), cellphone: onlyNumbers(cellPhone) || undefined,
      postcode: onlyNumbers(postcode), address: address.trim(),
      additional_address: additionalAddress.trim() || undefined,
      city_name: cityName.trim(), state_code: stateCode.trim().toUpperCase(), company_id: companyId,
      whapi_token: whapiToken.trim() || undefined,
      whapi_channel: whapiChannel.trim() || undefined,
      telegram_bot_token: telegramBotToken.trim() || undefined,
      telegram_chat_id: telegramChatId.trim() || undefined,
      whatsapp_provider: whatsappProvider,
      meta_phone_number_id: metaPhoneNumberId.trim() || undefined,
      meta_waba_id: metaWabaId.trim() || undefined,
      meta_bot_phone_number_id: metaBotPhoneNumberId.trim() || undefined,
      meta_access_token: metaAccessToken.trim() || undefined,
    };
    try {
      await onSave(data, clinic?.id);
      onOpenChange(false);
    } catch (error) {
      // Erros de salvamento ficam a cargo do pai
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide">
            {clinic ? "Editar Clínica" : "Nova Clínica"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="companyId">Empresa *</Label>
              <Select value={companyId} onValueChange={setCompanyId} disabled={companies.length === 1}>
                <SelectTrigger id="companyId" className="filter-input">
                  <SelectValue placeholder="Selecione uma empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.companyId && <p className="text-red-600 text-xs">{errors.companyId}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cnpj">CNPJ *</Label>
              <InputMask mask="99.999.999/9999-99" value={cnpj} onChange={(e) => setCnpj(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="cnpj" aria-invalid={!!errors.cnpj} />}
              </InputMask>
              {errors.cnpj && <p className="text-red-600 text-xs">{errors.cnpj}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="name">Nome *</Label>
              <Input className="filter-input" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Razão social" aria-invalid={!!errors.name} />
              {errors.name && <p className="text-red-600 text-xs">{errors.name}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="tradeName">Nome Fantasia *</Label>
              <Input className="filter-input" id="tradeName" value={tradeName} onChange={(e) => setTradeName(e.target.value)} placeholder="Nome fantasia" aria-invalid={!!errors.tradeName} />
              {errors.tradeName && <p className="text-red-600 text-xs">{errors.tradeName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="phone">Telefone *</Label>
              <InputMask mask="(99) 99999-9999" value={phone} onChange={(e) => setPhone(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="phone" placeholder="(00) 00000-0000" aria-invalid={!!errors.phone} />}
              </InputMask>
              {errors.phone && <p className="text-red-600 text-xs">{errors.phone}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cellPhone">Celular *</Label>
              <InputMask mask="(99) 99999-9999" value={cellPhone} onChange={(e) => setCellPhone(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="cellPhone" placeholder="(00) 00000-0000" aria-invalid={!!errors.cellPhone} />}
              </InputMask>
              {errors.cellPhone && <p className="text-red-600 text-xs">{errors.cellPhone}</p>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="postcode">CEP *</Label>
              <InputMask mask="99999-999" value={postcode} onChange={(e) => setPostcode(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="postcode" placeholder="00000-000" aria-invalid={!!errors.postcode} />}
              </InputMask>
              {errors.postcode && <p className="text-red-600 text-xs">{errors.postcode}</p>}
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="filter-label" htmlFor="address">Endereço *</Label>
              <Input className="filter-input" id="address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Rua, número" aria-invalid={!!errors.address} />
              {errors.address && <p className="text-red-600 text-xs">{errors.address}</p>}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="additionalAddress">Complemento</Label>
            <Input className="filter-input" id="additionalAddress" value={additionalAddress} onChange={(e) => setAdditionalAddress(e.target.value)} placeholder="Apartamento, sala, etc" />
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1 col-span-3">
              <Label className="filter-label" htmlFor="cityName">Cidade *</Label>
              <Input className="filter-input" id="cityName" value={cityName} onChange={(e) => setCityName(e.target.value)} placeholder="Nome da cidade" aria-invalid={!!errors.cityName} />
              {errors.cityName && <p className="text-red-600 text-xs">{errors.cityName}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="stateCode">Estado *</Label>
              <Input className="filter-input" id="stateCode" value={stateCode} onChange={(e) => setStateCode(e.target.value.toUpperCase())} placeholder="UF" maxLength={2} aria-invalid={!!errors.stateCode} />
              {errors.stateCode && <p className="text-red-600 text-xs">{errors.stateCode}</p>}
            </div>
          </div>

          <div className="border-t pt-3 mt-1">
            <p className="filter-label uppercase tracking-wide mb-2">Configuração WhatsApp</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="filter-label" htmlFor="whatsappProvider">Provedor</Label>
                <Select value={whatsappProvider} onValueChange={setWhatsappProvider}>
                  <SelectTrigger id="whatsappProvider" className="filter-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whapi">Whapi</SelectItem>
                    <SelectItem value="meta">Meta Cloud API</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {whatsappProvider === "whapi" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="filter-label" htmlFor="whapiToken">Token Whapi</Label>
                    <Input className="filter-input" id="whapiToken" value={whapiToken} onChange={(e) => setWhapiToken(e.target.value)} placeholder="Token de autenticação Whapi" aria-invalid={!!errors.whapiToken} />
                    {errors.whapiToken && <p className="text-red-600 text-xs">{errors.whapiToken}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="filter-label" htmlFor="whapiChannel">Canal Whapi</Label>
                    <Input className="filter-input" id="whapiChannel" value={whapiChannel} onChange={(e) => setWhapiChannel(e.target.value)} placeholder="Ex: ROCKET-Y5R88" aria-invalid={!!errors.whapiChannel} />
                    {errors.whapiChannel && <p className="text-red-600 text-xs">{errors.whapiChannel}</p>}
                  </div>
                </div>
              )}

              {whatsappProvider === "meta" && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="filter-label" htmlFor="metaPhoneNumberId">Phone Number ID</Label>
                      <Input className="filter-input" id="metaPhoneNumberId" value={metaPhoneNumberId} onChange={(e) => setMetaPhoneNumberId(e.target.value)} placeholder="Ex: 1166683556522913" />
                    </div>
                    <div className="space-y-1">
                      <Label className="filter-label" htmlFor="metaBotPhoneNumberId">Bot Phone Number ID</Label>
                      <Input className="filter-input" id="metaBotPhoneNumberId" value={metaBotPhoneNumberId} onChange={(e) => setMetaBotPhoneNumberId(e.target.value)} placeholder="ID do número do bot" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="filter-label" htmlFor="metaWabaId">WABA ID</Label>
                    <Input className="filter-input" id="metaWabaId" value={metaWabaId} onChange={(e) => setMetaWabaId(e.target.value)} placeholder="Ex: 1775321293182953" />
                  </div>
                  <div className="space-y-1">
                    <Label className="filter-label" htmlFor="metaAccessToken">Access Token</Label>
                    <Input className="filter-input" id="metaAccessToken" type="password" value={metaAccessToken} onChange={(e) => setMetaAccessToken(e.target.value)} placeholder={clinic ? "Deixe em branco para manter o atual" : "Token de acesso Meta"} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t pt-3 mt-1">
            <p className="filter-label uppercase tracking-wide mb-2">Configuração Telegram</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="filter-label" htmlFor="telegramBotToken">Token do Bot</Label>
                <Input
                  className="filter-input"
                  id="telegramBotToken"
                  value={telegramBotToken}
                  onChange={(e) => setTelegramBotToken(e.target.value)}
                  placeholder="123456:ABCdef..."
                />
              </div>
              <div className="space-y-1">
                <Label className="filter-label" htmlFor="telegramChatId">Chat ID</Label>
                <Input
                  className="filter-input"
                  id="telegramChatId"
                  value={telegramChatId}
                  onChange={(e) => setTelegramChatId(e.target.value)}
                  placeholder="Ex: 1562103759"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t gap-2">
          {Object.keys(errors).length > 0 && (
            <p className="text-red-600 text-xs">Corrija os campos marcados em vermelho.</p>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button size="sm" className="btn-header" onClick={handleSave}>
              {clinic ? "Salvar Alterações" : "Criar Clínica"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
